import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Divider,
} from "@mui/material";
import {
  Person as PersonIcon,
  LocationOn as LocationIcon,
  PriorityHigh as PriorityIcon,
  CalendarToday as CalendarIcon,
  Work as WorkIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import PropTypes from "prop-types";
import ImageCarousel from "./ImageCarousel";

const ObservationDetailsCard = ({
  observation,
  t,
  convertUTCToLocal,
  handleFileClick,
}) => {
  const images = observation.attachments?.filter((file) => file.is_image) || [];

  return (
    <Card
      elevation={2}
      sx={{
        mb: 4,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        bgcolor: "background.paper",
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          borderColor: "primary.main",
          boxShadow: 4,
        },
      }}
    >
      <Box
        sx={{
          borderBottom: "1px solid",
          borderColor: "divider",
          px: { xs: 2.5, sm: 3, md: 4 },
          py: { xs: 2, sm: 2.5 },
          bgcolor: (theme) => `${theme.palette.info.main}08`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <DescriptionIcon
            sx={{
              color: "info.main",
              fontSize: { xs: 20, sm: 22 },
            }}
          />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              fontSize: { xs: "1rem", sm: "1.125rem" },
            }}
          >
            {t("observationDetails.observationDetailsTitle")}
          </Typography>
        </Box>
      </Box>
      <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 4 } }}>
        {/* Main Content Layout: Details on left, Images on right */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: 3, lg: 4 },
            mb: 4,
          }}
        >
          {/* Left Side: Key Details */}
          <Box
            sx={{
              flex: { lg: "1" },
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {/* Reported By */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                bgcolor: (theme) => `${theme.palette.secondary.main}08`,
                border: "1px solid",
                borderColor: (theme) => `${theme.palette.secondary.main}20`,
              }}
            >
              <Avatar
                src={observation?.author?.avatar_url}
                sx={{
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  border: "2px solid",
                  borderColor: "secondary.main",
                }}
                alt={`${observation?.author?.first_name} ${observation.author?.last_name}`}
              >
                <PersonIcon />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {t("observationDetails.reportedByLabel")}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    fontSize: { xs: "0.95rem", sm: "1.05rem" },
                  }}
                >
                  {observation.author?.first_name}{" "}
                  {observation.author?.last_name}
                </Typography>
              </Box>
            </Box>

            {/* Site */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                bgcolor: (theme) => `${theme.palette.info.main}08`,
                border: "1px solid",
                borderColor: (theme) => `${theme.palette.info.main}20`,
              }}
            >
              <Box
                sx={{
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  borderRadius: "50%",
                  bgcolor: (theme) => `${theme.palette.info.main}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LocationIcon
                  sx={{
                    color: "info.main",
                    fontSize: { xs: 20, sm: 22 },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {t("observationDetails.siteLabel")}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    fontSize: { xs: "0.95rem", sm: "1.05rem" },
                  }}
                >
                  {observation.site?.name || t("observationDetails.na")}
                </Typography>
              </Box>
            </Box>

            {/* Priority */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                bgcolor: (theme) => `${theme.palette.warning.main}08`,
                border: "1px solid",
                borderColor: (theme) => `${theme.palette.warning.main}20`,
              }}
            >
              <Box
                sx={{
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  borderRadius: "50%",
                  bgcolor: (theme) => `${theme.palette.warning.main}15`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PriorityIcon
                  sx={{
                    color: "warning.main",
                    fontSize: { xs: 20, sm: 22 },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {t("observationDetails.priorityLabel")}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    fontSize: { xs: "0.95rem", sm: "1.05rem" },
                  }}
                >
                  {observation.observation_priority?.name ||
                    t("observationDetails.na")}
                </Typography>
              </Box>
            </Box>

            {/* Reported At */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: { xs: 2, sm: 2.5 },
                borderRadius: 3,
                bgcolor: "action.hover",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  width: { xs: 40, sm: 44 },
                  height: { xs: 40, sm: 44 },
                  borderRadius: "50%",
                  bgcolor: "action.selected",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CalendarIcon
                  sx={{
                    color: "text.secondary",
                    fontSize: { xs: 20, sm: 22 },
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                  }}
                >
                  {t("observationDetails.reportedAtLabel")}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                    fontSize: { xs: "0.95rem", sm: "1.05rem" },
                  }}
                >
                  {convertUTCToLocal(observation?.created_at)}
                </Typography>
              </Box>
            </Box>

            {/* Work Permit (if exists) */}
            {observation.work_permit && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: { xs: 2, sm: 2.5 },
                  borderRadius: 3,
                  bgcolor: (theme) => `${theme.palette.success.main}08`,
                  border: "1px solid",
                  borderColor: (theme) => `${theme.palette.success.main}20`,
                }}
              >
                <Box
                  sx={{
                    width: { xs: 40, sm: 44 },
                    height: { xs: 40, sm: 44 },
                    borderRadius: "50%",
                    bgcolor: (theme) => `${theme.palette.success.main}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <WorkIcon
                    sx={{
                      color: "success.main",
                      fontSize: { xs: 20, sm: 22 },
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                    }}
                  >
                    {t("observationDetails.workPermitLabel")}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      fontSize: { xs: "0.95rem", sm: "1.05rem" },
                    }}
                  >
                    {observation.work_permit.title} (
                    {observation.work_permit.work_type})
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Right Side: Image Carousel (if images exist) */}
          {images.length > 0 && (
            <Box
              sx={{
                flex: { lg: "1" },
                maxWidth: { lg: "50%" },
                display: "flex",
                alignItems: "center", // Vertically center the carousel
                justifyContent: "center",
              }}
            >
              <ImageCarousel
                images={images}
                onImageClick={handleFileClick}
                convertUTCToLocal={convertUTCToLocal}
                t={t}
              />
            </Box>
          )}
        </Box>

        <Divider
          sx={{
            my: 3,
            borderColor: "divider",
          }}
        />

        {/* Description Section */}
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              mb: 2,
            }}
          >
            <DescriptionIcon
              sx={{
                color: "text.secondary",
                fontSize: { xs: 18, sm: 20 },
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: { xs: "1rem", sm: "1.125rem" },
              }}
            >
              {t("observationDetails.descriptionLabel")}
            </Typography>
          </Box>
          <Box
            sx={{
              p: { xs: 2, sm: 3 },
              bgcolor: "action.hover",
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              position: "relative",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                bgcolor: "primary.main",
                borderRadius: "4px 0 0 4px",
              },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                whiteSpace: "pre-line",
                lineHeight: 1.6,
                fontSize: { xs: "0.875rem", sm: "1rem" },
                color: observation.description
                  ? "text.primary"
                  : "text.secondary",
                fontStyle: observation.description ? "normal" : "italic",
              }}
            >
              {observation.description || t("observationDetails.noDescription")}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

ObservationDetailsCard.propTypes = {
  observation: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  convertUTCToLocal: PropTypes.func.isRequired,
  handleFileClick: PropTypes.func.isRequired,
};

export default ObservationDetailsCard;
